import { AudioLines, LogOut, Menu, Moon, Sun } from 'lucide-react';
import { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';
import { useAuth, useUser } from '../auth/AuthProvider';
import { ROLE_LABELS, navItemsFor } from '../auth/roles';
import { Avatar, Button } from '../components';
import { cn } from '../lib/cn';
import { useTheme } from '../lib/theme';

export function AppLayout() {
  const user = useUser();
  const { logout } = useAuth();
  const { theme, toggle } = useTheme();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const items = navItemsFor(user.role);

  return (
    <div className="flex min-h-screen">
      <aside
        className={cn(
          'bg-sidebar-gradient fixed inset-y-0 left-0 z-40 flex w-56 shrink-0 flex-col transition-transform md:sticky md:top-0 md:h-screen md:translate-x-0',
          menuOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-14 items-center gap-2.5 px-5">
          <span className="bg-brand-gradient grid h-7 w-7 place-items-center rounded-lg">
            <AudioLines aria-hidden className="h-4 w-4 text-white" />
          </span>
          <span className="text-sm font-semibold text-sidebar-ink">Sales Copilot</span>
        </div>
        <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-2" aria-label="Main">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setMenuOpen(false)}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white',
                  isActive ? 'bg-white/15 font-medium text-sidebar-ink' : 'text-sidebar-muted hover:bg-white/10 hover:text-sidebar-ink',
                )
              }
            >
              <item.icon aria-hidden className="h-4 w-4 shrink-0" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 p-3">
          <div className="flex items-center gap-2.5 px-2 py-1.5">
            <Avatar name={user.name} size="md" className="bg-white/15 text-sidebar-ink" />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-sidebar-ink">{user.name}</div>
              <div className="truncate text-xs text-sidebar-muted">
                {ROLE_LABELS[user.role]}
                {user.teamName ? ` · ${user.teamName}` : ''}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => void logout()}
            className="mt-1 flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-sidebar-muted transition-colors hover:bg-white/10 hover:text-sidebar-ink focus-visible:outline-2 focus-visible:outline-white"
          >
            <LogOut aria-hidden className="h-4 w-4" />
            Log out
          </button>
        </div>
      </aside>
      {menuOpen && <div className="fixed inset-0 z-30 bg-black/40 md:hidden" onClick={() => setMenuOpen(false)} />}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-14 items-center gap-3 bg-page/80 px-4 backdrop-blur md:px-6">
          <Button variant="ghost" size="sm" className="md:hidden" aria-label="Open menu" onClick={() => setMenuOpen(true)}>
            <Menu aria-hidden className="h-4 w-4" />
          </Button>
          <div className="flex-1" />
          <Button variant="ghost" size="sm" onClick={toggle} aria-label="Toggle theme" title="Toggle light/dark theme">
            {theme === 'dark' ? <Sun aria-hidden className="h-4 w-4" /> : <Moon aria-hidden className="h-4 w-4" />}
            {theme === 'dark' ? 'Light' : 'Dark'}
          </Button>
        </header>
        <main className="mx-auto w-full max-w-[1400px] flex-1 px-4 pb-8 md:px-6">
          {/* Remount per route so each page fades and rises in. */}
          <div key={location.pathname} className="rise">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
